/**
 * Collection infinite scroll + "back to where you were".
 *
 * - <collection-load-more> sits under #product-grid (main-collection-product-grid). When it gets
 *   close to the viewport, the next page is fetched via the Section Rendering API and its cards are
 *   appended. The fetched page brings its own <collection-load-more>, which continues the chain.
 * - Clicking a product card remembers which page / card the shopper was on. When they come back
 *   with the browser Back button, the same pages are loaded again and the card is scrolled back to
 *   the same position on screen.
 */
(() => {
  if (customElements.get('collection-load-more')) return;

  const STORAGE_PREFIX = 'collection-scroll:';
  const STATE_TTL = 30 * 60 * 1000;
  // Start loading well before the shopper reaches the end of the grid.
  const PRELOAD_MARGIN = '0px 0px 1200px 0px';

  const parser = new DOMParser();
  let restoring = false;

  const storageKey = () => {
    const params = new URLSearchParams(window.location.search);
    params.delete('page');
    return `${STORAGE_PREFIX}${window.location.pathname}?${params.toString()}`;
  };

  const sectionUrl = (href, sectionId) => {
    const url = new URL(href, window.location.href);
    url.searchParams.set('section_id', sectionId);
    return url.toString();
  };

  const pageUrl = (page, sectionId) => {
    const url = new URL(window.location.href);
    url.searchParams.set('page', page);
    return sectionUrl(url, sectionId);
  };

  const fetchPage = async (url, signal) => {
    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const html = parser.parseFromString(await response.text(), 'text/html');
    return {
      items: Array.from(html.querySelectorAll('#product-grid > .grid__item')),
      loadMore: html.querySelector('collection-load-more'),
    };
  };

  // Appends a fetched page and swaps `current` for the page's own load-more (or removes it on the last page).
  const appendPage = ({ items, loadMore }, current, animate = true) => {
    const grid = document.getElementById('product-grid');
    if (!grid) return null;

    items.forEach((item) => {
      // Inline card scripts are already initialised globally and would not run from here anyway.
      item.querySelectorAll('script').forEach((script) => script.remove());
      grid.appendChild(item);
    });

    if (animate && typeof initializeScrollAnimationTrigger === 'function') {
      // Only register the new cards, so already-revealed cards above are not re-hidden.
      initializeScrollAnimationTrigger({
        getElementsByClassName: () => items.filter((item) => item.classList.contains('scroll-trigger')),
      });
    }

    if (loadMore) {
      current.replaceWith(loadMore);
      return loadMore;
    }
    current.remove();
    return null;
  };

  class CollectionLoadMore extends HTMLElement {
    connectedCallback() {
      this.link = this.querySelector('[data-load-more-link]');
      this.spinner = this.querySelector('.loading__spinner');
      if (!this.link) return;

      this.link.addEventListener('click', (event) => {
        event.preventDefault();
        this.load();
      });

      if (!('IntersectionObserver' in window)) return;
      this.classList.add('is-auto');
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.load();
        },
        { rootMargin: PRELOAD_MARGIN }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.controller) this.controller.abort();
    }

    // Re-run the visibility check; IntersectionObserver reports the current state on observe().
    recheck() {
      if (!this.observer) return;
      this.observer.unobserve(this);
      this.observer.observe(this);
    }

    setLoading(isLoading) {
      this.loading = isLoading;
      this.classList.toggle('is-loading', isLoading);
      if (this.spinner) this.spinner.classList.toggle('hidden', !isLoading);
    }

    async load() {
      if (this.loading || restoring) return;
      this.setLoading(true);
      this.controller = new AbortController();

      try {
        const page = await fetchPage(sectionUrl(this.link.href, this.dataset.sectionId), this.controller.signal);
        if (this.isConnected) appendPage(page, this);
      } catch (error) {
        if (error.name === 'AbortError') return;
        // Stop auto-loading and show the button so the shopper can retry manually.
        if (this.observer) this.observer.disconnect();
        this.classList.remove('is-auto');
        this.setLoading(false);
      }
    }
  }

  customElements.define('collection-load-more', CollectionLoadMore);

  // Remember the clicked card so Back can return to it.
  document.addEventListener(
    'click',
    (event) => {
      const link = event.target.closest('#product-grid a[href]');
      if (!link) return;
      const item = link.closest('.grid__item');
      const url = new URL(link.href, window.location.href);
      if (!item || !url.pathname.includes('/products/')) return;

      try {
        sessionStorage.setItem(
          storageKey(),
          JSON.stringify({
            page: Number(item.dataset.page) || 1,
            product: url.pathname,
            offset: item.getBoundingClientRect().top,
            time: Date.now(),
          })
        );
      } catch (error) {
        // Storage unavailable (private mode etc.) – Back just behaves like a normal page load.
      }
    },
    true
  );

  const findItem = (grid, state) =>
    Array.from(grid.querySelectorAll(`.grid__item[data-page="${state.page}"]`)).find((item) =>
      Array.from(item.querySelectorAll('a[href]')).some(
        (link) => new URL(link.href, window.location.href).pathname === state.product
      )
    );

  const restore = async () => {
    const navigation = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
    if (!navigation || navigation.type !== 'back_forward') return;

    let state;
    try {
      state = JSON.parse(sessionStorage.getItem(storageKey()));
    } catch (error) {
      return;
    }
    if (!state || Date.now() - state.time > STATE_TTL) return;

    const grid = document.getElementById('product-grid');
    const firstItem = grid && grid.querySelector('.grid__item');
    if (!firstItem) return;

    restoring = true;
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

    let userScrolled = false;
    const onUserScroll = () => (userScrolled = true);
    ['wheel', 'touchstart', 'keydown'].forEach((type) =>
      window.addEventListener(type, onUserScroll, { once: true, passive: true })
    );

    try {
      let loadMore = document.querySelector('#ProductGridContainer collection-load-more');
      const pages = [];
      for (let page = (Number(firstItem.dataset.page) || 1) + 1; loadMore && page <= state.page; page++) {
        pages.push(page);
      }

      // Fetch in parallel, append in order.
      const results = await Promise.all(pages.map((page) => fetchPage(pageUrl(page, loadMore.dataset.sectionId))));
      results.forEach((result) => {
        if (loadMore) loadMore = appendPage(result, loadMore, false);
      });

      const align = () => {
        const target = findItem(grid, state);
        if (target && !userScrolled) {
          window.scrollTo(0, target.getBoundingClientRect().top + window.scrollY - state.offset);
        }
      };
      align();
      // Content above the grid (banner images etc.) may still shift until load; re-align once then.
      if (document.readyState !== 'complete') window.addEventListener('load', align, { once: true });
    } catch (error) {
      // Leave the page as rendered.
    } finally {
      restoring = false;
      const resetScrollRestoration = () => {
        if ('scrollRestoration' in history) history.scrollRestoration = 'auto';
      };
      document.readyState === 'complete'
        ? resetScrollRestoration()
        : window.addEventListener('load', () => setTimeout(resetScrollRestoration), { once: true });

      const loadMore = document.querySelector('#ProductGridContainer collection-load-more');
      if (loadMore) loadMore.recheck();
    }
  };

  restore();
})();
